import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import axios from "axios";

import {
  API_URL,
  getPosHeaders,
} from "../api";

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const paymentLabel = (method) => {
  const labels = {
    cash: "Efectivo",
    card: "Tarjeta",
    transfer: "Transferencia",
    credit: "Crédito",
    mixed: "Pago mixto",
  };

  return labels[method] || method;
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

function SaleReturnModal({
  sale,
  mode,
  onClose,
  onCompleted,
}) {
  const { company } = useParams();

  const submittingRef = useRef(false);

  const isDetail = mode === "detail";
  const isCancellation = mode === "cancel";

  const saleItems = useMemo(
    () => (Array.isArray(sale?.items) ? sale.items : []),
    [sale?.items]
  );

  const availableItems = useMemo(
    () =>
      saleItems.filter(
        (item) => Number(item.available_to_return) > 0
      ),
    [saleItems]
  );

  const paymentMethods = useMemo(
    () => [
      ...new Set(
        (sale?.payments || [])
          .map((payment) => payment.payment_method)
          .filter(Boolean)
      ),
    ],
    [sale?.payments]
  );

  const [quantities, setQuantities] = useState({});
  const [refundMethod, setRefundMethod] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const initialQuantities = {};

    saleItems.forEach((item) => {
      initialQuantities[item.id] = isCancellation
        ? Math.max(0, Number(item.available_to_return) || 0)
        : 0;
    });

    setQuantities(initialQuantities);
    setRefundMethod(paymentMethods[0] || "");
    setReason("");
    setErrorMessage("");
  }, [sale?.id, saleItems, isCancellation, paymentMethods]);

  const refundTotal = useMemo(
    () =>
      saleItems.reduce((total, item) => {
        const quantity = Number(quantities[item.id] || 0);
        const unitPrice = Number(item.unit_price || 0);

        return total + quantity * unitPrice;
      }, 0),
    [saleItems, quantities]
  );

  const updateQuantity = (item, value) => {
    const available = Math.max(
      0,
      Number(item.available_to_return) || 0
    );

    const parsedQuantity = Number(value || 0);

    const quantity = Number.isFinite(parsedQuantity)
      ? Math.min(available, Math.max(0, parsedQuantity))
      : 0;

    setQuantities((current) => ({
      ...current,
      [item.id]: quantity,
    }));
  };

  const submitReturn = async (event) => {
    event.preventDefault();

    if (isDetail || submittingRef.current) {
      return;
    }

    setErrorMessage("");

    if (!sale?.id) {
      setErrorMessage("La venta no tiene un identificador válido.");
      return;
    }

    const headers = getPosHeaders(company);

    if (!headers.Authorization) {
      setErrorMessage(
        "Tu sesión POS no está disponible. Inicia sesión nuevamente."
      );
      return;
    }

    const items = saleItems
      .map((item) => ({
        sale_item_id: item.id,
        quantity: Number(quantities[item.id] || 0),
      }))
      .filter((item) => item.quantity > 0);

    if (items.length === 0) {
      setErrorMessage("Selecciona al menos un producto.");
      return;
    }

    const hasInvalidQuantity = items.some((selectedItem) => {
      const originalItem = saleItems.find(
        (item) => item.id === selectedItem.sale_item_id
      );

      return (
        !Number.isFinite(selectedItem.quantity) ||
        !originalItem ||
        selectedItem.quantity >
          Number(originalItem.available_to_return)
      );
    });

    if (hasInvalidQuantity) {
      setErrorMessage(
        "Revisa las cantidades disponibles para reintegrar."
      );
      return;
    }

    if (!paymentMethods.includes(refundMethod)) {
      setErrorMessage("Selecciona un método de reintegro válido.");
      return;
    }

    if (reason.trim().length < 3) {
      setErrorMessage(
        "Escribe un motivo de al menos 3 caracteres."
      );
      return;
    }

    submittingRef.current = true;
    setSaving(true);

    try {
      let response;

      try {
        response = await axios.post(
          `${API_URL}/pos/sales/${sale.id}/returns`,
          {
            items,
            refund_method: refundMethod,
            reason: reason.trim(),
          },
          {
            headers,
          }
        );
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
        return;
      }

      // La devolución ya fue registrada.
      // Un fallo al actualizar la pantalla no debe invitar a repetirla.
      try {
        await onCompleted(
          response.data.message ||
            "Reintegro registrado correctamente."
        );
      } catch (error) {
        console.error(
          "El reintegro fue registrado, pero falló la actualización:",
          error
        );

        setErrorMessage(
          "El reintegro fue registrado. Cierra esta ventana y " +
            "actualiza las ventas antes de realizar otra operación."
        );
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="sale-modal-backdrop">
      <section
        className="sale-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sale-modal-title"
        aria-busy={saving}
      >
        <button
          type="button"
          className="sale-modal-close"
          onClick={onClose}
          disabled={saving}
          aria-label="Cerrar ventana"
        >
          ×
        </button>

        <div className="sale-modal-header">
          <div>
            <span>
              Venta {sale.document_number || `#${sale.id}`}
            </span>

            <h2 id="sale-modal-title">
              {isDetail
                ? "Detalle de venta"
                : isCancellation
                  ? "Anular venta"
                  : "Registrar reintegro"}
            </h2>
          </div>

          <span
            className={`sale-status sale-status-${sale.status}`}
          >
            {sale.status === "cancelled"
              ? "Anulada"
              : sale.status === "partially_refunded"
                ? "Reintegro parcial"
                : "Completada"}
          </span>
        </div>

        <div className="sale-detail-data">
          <div>
            <span>Cliente</span>
            <strong>
              {sale.customer_name || "Cliente general"}
            </strong>
          </div>

          <div>
            <span>Fecha</span>
            <strong>
              {sale.created_at
                ? new Date(sale.created_at).toLocaleString("es-CO")
                : "-"}
            </strong>
          </div>

          <div>
            <span>Total venta</span>
            <strong>{money(sale.total)}</strong>
          </div>
        </div>

        {isDetail ? (
          <>
            <h3 className="sale-modal-subtitle">
              Productos vendidos
            </h3>

            <div className="sale-return-items">
              {saleItems.map((item) => (
                <div
                  className="sale-return-item"
                  key={item.id}
                >
                  <div>
                    <strong>{item.product_name}</strong>
                    <small>
                      {item.sku} · {money(item.unit_price)}
                    </small>
                  </div>

                  <div className="sale-item-quantities">
                    <span>Vendido: {item.quantity}</span>
                    <span>
                      Reintegrado: {item.returned_quantity}
                    </span>
                    <b>
                      Disponible: {item.available_to_return}
                    </b>
                  </div>
                </div>
              ))}
            </div>

            {sale.returns?.length > 0 && (
              <>
                <h3 className="sale-modal-subtitle">
                  Historial de reintegros
                </h3>

                <div className="sale-returns-history">
                  {sale.returns.map((saleReturn) => (
                    <div key={saleReturn.id}>
                      <strong>
                        {money(saleReturn.total)}
                      </strong>
                      <span>
                        {paymentLabel(saleReturn.refund_method)}
                        {" · "}
                        {saleReturn.reason}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <button
              type="button"
              className="btn-sale-modal-secondary"
              onClick={onClose}
            >
              Cerrar
            </button>
          </>
        ) : (
          <form onSubmit={submitReturn}>
            <p className="sale-return-help">
              {isCancellation
                ? "Se reintegrarán todas las unidades pendientes de esta venta."
                : "Indica las cantidades que el cliente está devolviendo."}
            </p>

            <div className="sale-return-items">
              {availableItems.length === 0 ? (
                <p className="sale-no-available-items">
                  No hay productos disponibles para reintegrar.
                </p>
              ) : (
                availableItems.map((item) => (
                  <div
                    className="sale-return-item"
                    key={item.id}
                  >
                    <div>
                      <strong>{item.product_name}</strong>
                      <small>
                        {item.sku} · {money(item.unit_price)}
                      </small>
                      <small>
                        Disponible para reintegro:{" "}
                        {item.available_to_return}
                      </small>
                    </div>

                    <input
                      type="number"
                      min="0"
                      step="any"
                      max={item.available_to_return}
                      value={quantities[item.id] || 0}
                      onChange={(event) =>
                        updateQuantity(item, event.target.value)
                      }
                      disabled={isCancellation || saving}
                      aria-label={`Cantidad a reintegrar de ${item.product_name}`}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="sale-return-form-grid">
              <div>
                <label htmlFor="refund_method">
                  Método de reintegro
                </label>

                <select
                  id="refund_method"
                  value={refundMethod}
                  onChange={(event) =>
                    setRefundMethod(event.target.value)
                  }
                  disabled={saving}
                  required
                >
                  <option value="" disabled>
                    Selecciona un método
                  </option>

                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {paymentLabel(method)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label>Total estimado a reintegrar</label>

                <div className="sale-refund-total">
                  {money(refundTotal)}
                </div>
              </div>
            </div>

            <div className="sale-return-reason">
              <label htmlFor="return_reason">Motivo</label>

              <textarea
                id="return_reason"
                rows="3"
                value={reason}
                onChange={(event) =>
                  setReason(event.target.value)
                }
                placeholder="Ejemplo: Producto en mal estado"
                minLength={3}
                disabled={saving}
                required
              />
            </div>

            {errorMessage && (
              <p className="sale-return-error" role="alert">
                {errorMessage}
              </p>
            )}

            <div className="sale-modal-actions">
              <button
                type="button"
                className="btn-sale-modal-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className={
                  isCancellation
                    ? "btn-confirm-cancellation"
                    : "btn-confirm-return"
                }
                disabled={
                  saving ||
                  availableItems.length === 0 ||
                  paymentMethods.length === 0
                }
              >
                {saving
                  ? "Procesando..."
                  : isCancellation
                    ? "Confirmar anulación"
                    : "Confirmar reintegro"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

export default SaleReturnModal;